import {
  Pill,
  TableCell,
  TableRow,
  Tooltip,
  Typography
} from '@sanlam-glacier/app-common/components';
import { Table, TableBody, TableHeader } from 'components/organisms/table';
import styles from './table.module.scss';
import { ExposureTableProps } from './exposure-table.type';
import { mapAlignment } from './exposure-table.function';
import clsx from 'clsx';
import { Text } from 'components/molecules/text/Text.tsx';
import { Block, Grid } from '@sanlam-glacier/app-common/modules/grid';

export const ExposureTable = (table: ExposureTableProps) => {
  const { columns, rows } = table;

  const headers =
    rows.length > 0
      ? columns.map((column, idx) => ({
          label: column.displayValue,
          align: column.align,
          className: idx === 0 ? styles.firstColumn : undefined
        }))
      : [];

  const renderDataRow = (row: (typeof rows)[number], rowIndex: number) => (
    <TableRow key={`table-row-${rowIndex}`} hoverable={false}>
      {row.values.map((value, valueIndex) => (
        <TableCell
          key={`table-cell-${rowIndex}-${valueIndex}`}
          align={mapAlignment(value.align)}
          className={valueIndex === 0 ? styles.firstColumnData : undefined}
        >
          {valueIndex === 0
            ? renderFirstColumnData(value, valueIndex, rowIndex)
            : value.displayValue}
        </TableCell>
      ))}
    </TableRow>
  );

  const renderFirstColumnData = (
    value: (typeof rows)[number]['values'][number],
    valueIndex: number,
    rowIndex: number
  ) => {
    if (value.displaySubValue) {
      const isCurrentProposal = true;
      return (
        <>
          <Tooltip
            content={value.displayValue}
            key={`tooltip-${rowIndex}-${valueIndex}`}
            className={styles.fundNameTooltip}
          >
            <span>
              <Grid row>
                <Block lg={11} sm={8}>
                  <Text variant={Typography.VARIANT.B4} weight={Typography.WEIGHT.MEDIUM}>
                    {value.displaySubValue}
                  </Text>
                  <Text variant={Typography.VARIANT.B4} weight={Typography.WEIGHT.NORMAL}>
                    {value.displayValue}
                  </Text>
                </Block>
                {isCurrentProposal && (
                  <Block justify={'end'} alignItems={'center'}>
                    <Pill variant={Pill.VARIANT.TERTIARY}>Current Proposal</Pill>
                  </Block>
                )}
              </Grid>
            </span>
          </Tooltip>
        </>
      );
    }
    return (
      <Tooltip
        content={value.displayValue}
        key={`tooltip-${rowIndex}-${valueIndex}`}
        className={styles.fundNameTooltip}
      >
        <span>{value.displayValue}</span>
      </Tooltip>
    );
  };

  const renderSummaryRow = () => (
    <TableRow key='summary' hoverable={false} className={styles.tableWeightedAllocationRow}>
      {columns.map((column, colIndex) => {
        if (colIndex === 0) {
          return (
            <TableCell key='table-summary-title' align={mapAlignment(column.align)}>
              <div className={styles.bold}>Weighted allocation</div>
              <div className={styles.cellSpace} />% Allowed (Maximum)
            </TableCell>
          );
        }

        const weighted = column.weightedPercentage ?? 0;
        const isOverMax = column.maximumPercentage ? weighted > column.maximumPercentage : false;

        return (
          <TableCell
            key={`summary-cell-${colIndex}`}
            align={mapAlignment(column.align)}
            className={isOverMax ? styles.errorCell : undefined}
          >
            <div className={clsx(styles.bold, isOverMax ? styles.errorLabel : '')}>
              {weighted.toFixed(2)}
            </div>
            <div className={styles.cellSpace} />
            {column.maximumPercentage?.toFixed(2) ?? ''}
          </TableCell>
        );
      })}
    </TableRow>
  );

  return (
    <div className={styles.tableContainer}>
      <div className={styles.table}>
        <Table>
          <TableHeader
            headers={headers}
            tableHeaderClassName={styles.tableHeader}
            hoverable={false}
          />
          <TableBody rows={rows} renderRow={renderDataRow}>
            {renderSummaryRow()}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
